import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Flame, ArrowRight, Eye, Heart, Image as ImageIcon, MessageSquare } from 'lucide-react';

const TrendingImage = ({ blog }) => {
  const [hasError, setHasError] = useState(false);

  return (
    <div className="relative h-44 overflow-hidden bg-[#f3effa]">
      {!hasError ? (
        <img
          src={blog.image}
          alt={blog.title}
          className="h-full w-full object-cover"
          referrerPolicy="no-referrer"
          loading="lazy"
          onError={() => setHasError(true)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-[#ede4fb] to-[#f8e8df] text-[#9b55dd]">
          <div className="flex flex-col items-center gap-2">
            <ImageIcon className="h-8 w-8" />
            <span className="text-xs font-semibold uppercase tracking-[0.16em]">Blog Image</span>
          </div>
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-[#120d1d]/20 via-transparent to-transparent" />
      <span className="absolute left-4 top-4 flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-[#9448ed] to-[#ff7a3f] text-sm font-bold text-white shadow-lg">
        {blog.trendingRank}
      </span>
    </div>
  );
};

const TrendingBlogCard = ({ blog }) => {
  return (
    <article className="w-[78vw] max-w-[300px] shrink-0 overflow-hidden rounded-[24px] border border-gray-200 bg-white shadow-[0_14px_30px_rgba(15,23,42,0.06)] sm:w-auto sm:max-w-none sm:shrink">
      <Link to={`/blogs/${blog.slug}`} className="block">
        <TrendingImage blog={blog} />
      </Link>
      <div className="p-4">
        <h4 className="text-[1.05rem] font-bold leading-snug text-[#211731]">{blog.title}</h4>
        <p className="mt-3 text-sm text-[#766a86]">
          {blog.views} | {blog.readTime} read
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2 text-[0.76rem] font-semibold">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#fff4f8] px-2.5 py-1 text-[#db4f89]">
            <Heart className="h-3.5 w-3.5" />
            {blog.likes}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#f5f1ff] px-2.5 py-1 text-[#8653df]">
            <MessageSquare className="h-3.5 w-3.5" />
            {blog.comments}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[#edf4ff] px-2.5 py-1 text-[#4b79d9]">
            <Eye className="h-3.5 w-3.5" />
            {blog.views}
          </span>
        </div>
      </div>
    </article>
  );
};

const TrendingBlogs = ({ blogs }) => {
  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.scrollWidth / blogs.length;
    const index = Math.round(el.scrollLeft / cardWidth);
    setActiveIndex(Math.min(index, blogs.length - 1));
  };

  return (
    <section className="rounded-[30px] border border-gray-100 bg-white p-5 shadow-sm sm:p-6">
      <div className="mb-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Flame className="h-5 w-5 text-[#ff6c49]" />
          <h3 className="text-xl font-bold text-[#2a1f38]">Trending Now</h3>
        </div>
        <Link
          to="/blogs?sort=trending"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#a43ddd] transition-colors hover:text-[#ff7f32]"
        >
          View All
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>

      {/* Mobile - horizontal scroll */}
      <div
        ref={scrollRef}
        onScroll={handleScroll}
        className="flex gap-4 overflow-x-auto pb-2 sm:hidden"
        style={{ scrollSnapType: 'x mandatory', WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        {blogs.map((blog) => (
          <div key={blog.id} style={{ scrollSnapAlign: 'start' }}>
            <TrendingBlogCard blog={blog} />
          </div>
        ))}
      </div>

      {/* Dot indicators - mobile only */}
      {blogs.length > 1 && (
        <div className="mt-3 flex items-center justify-center gap-1.5 sm:hidden">
          {blogs.map((_, i) => (
            <button
              key={i}
              type="button"
              onClick={() => {
                const el = scrollRef.current;
                if (!el) return;
                const cardWidth = el.scrollWidth / blogs.length;
                el.scrollTo({ left: cardWidth * i, behavior: 'smooth' });
                setActiveIndex(i);
              }}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === activeIndex
                  ? 'w-5 bg-[#9a51a0]'
                  : 'w-1.5 bg-gray-300'
              }`}
            />
          ))}
        </div>
      )}

      {/* Desktop - grid */}
      <div className="hidden gap-5 sm:grid lg:grid-cols-3">
        {blogs.map((blog) => (
          <TrendingBlogCard key={blog.id} blog={blog} />
        ))}
      </div>
    </section>
  );
};

export default TrendingBlogs;
